import type { DeepString } from "@/lib/i18n";

/**
 * CPolls - polskie tłumaczenia (Interaktywne formularze / ankiety).
 *
 * UI statyczne tutaj; dane (nazwy ankiet, pytań, odpowiedzi) z backendu.
 * `remainingQuestions` używa polskiej odmiany "pytanie/pytania/pytań".
 * `formatNextFill` formatuje datę kolejnego wypełnienia (z `next_fill_at`).
 */
export const pl = {
  emptyData: "Brak danych do wyświetlenia.",
  list: {
    empty: "Brak ankiet do wyświetlenia.",
    fillOut: "Wypełnij",
    showResults: "Wyniki",
    alreadyFilled: "Wypełniona",
  },
  poll: {
    guestHeading: "Przedstaw się na początek",
    labels: {
      email: "Adres e-mail",
      name: "Imię",
      surname: "Nazwisko",
      telephone: "Telefon",
      justification: "Uzasadnienie odpowiedzi",
    },
    back: "Wróć do ankiet",
    next: "Przejdź dalej",
    end: "Zakończ",
    submitting: "Zapisywanie...",
    alreadyFilled: "Ta ankieta była już wypełniana przez tego użytkownika.",
    rankingHint: "Przeciągnij elementy lub użyj strzałek, aby ustalić kolejność.",
    moveUp: "Przesuń w górę",
    moveDown: "Przesuń w dół",
    timeUp: "Czas na wypełnienie ankiety minął - odpowiedzi zostały zapisane.",
    errors: {
      required: "To pole jest wymagane.",
      email: "Podaj poprawny adres e-mail.",
      emailTaken: "Ten adres e-mail jest już zajęty.",
      questionRequired: "Odpowiedź na to pytanie jest wymagana.",
      submit: "Nie udało się zapisać ankiety. Spróbuj ponownie.",
    },
    /** `nextFill("17.06.2026 12:30")` -> "Następne wypełnienie możliwe po ...". */
    nextFill: (date: string): string => `Następne wypełnienie możliwe po ${date}`,
    /** `remainingTime("04:59")` -> "Czas pozostały na wypełnienie ankiety: 04:59". */
    remainingTime: (time: string): string =>
      `Czas pozostały na wypełnienie ankiety: ${time}`,
    /** `remainingQuestions(3, 40)` -> "Do końca ankiety pozostało: 3 pytania (40%)". */
    remainingQuestions: (remaining: number, percent: number): string => {
      const abs = Math.abs(remaining);
      const last = abs % 10;
      const last2 = abs % 100;
      let word = "pytań";
      if (abs === 1) word = "pytanie";
      else if (last >= 2 && last <= 4 && !(last2 >= 12 && last2 <= 14)) word = "pytania";
      return `Do końca ankiety pozostało: ${remaining} ${word} (${percent}%)`;
    },
  },
  completed: {
    thankYou: "Dziękujemy za wypełnienie ankiety !!!",
    showResults: "Zobacz wyniki",
    backToList: "Wróć do ankiet",
  },
  score: {
    backToList: "Wróć do ankiet",
    noData: "Brak wyników do wyświetlenia.",
    votes: "Liczba głosów",
    percent: "Udział procentowy",
  },
  /** `formatNextFill("2026-06-17 12:30:00")` -> "17.06.2026 12:30". */
  formatNextFill: (raw: string): string => {
    const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(raw);
    if (!m) return raw;
    return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
  },
} as const;

export type CPollsTranslations = DeepString<typeof pl>;
